import { NotificationBanner } from '@mntn/select-components'
import { Icon, Text } from '@mntn/ui-components'
import { useCallback, useState } from 'react'
import { DetailModal } from '@/components/dashboard/detail-modal'
import { Button } from '@/components/shared'
import type {
  ArchiveCampaignCallback,
  CreateCampaignCallbacks,
  DuplicateCampaignCallback,
} from '@/components/shared/types'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'

type AllocationBannerProps = {
  alertOrders: OrderItem[]
  onDuplicateCampaign: DuplicateCampaignCallback
  onArchiveCampaign: ArchiveCampaignCallback
  isLoading?: boolean
} & CreateCampaignCallbacks

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const AllocationBanner: React.FC<AllocationBannerProps> = props => {
  const { alertOrders, onDuplicateCampaign, onArchiveCampaign, isLoading = false } = props
  const [isDismissed, setIsDismissed] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedOrderItem, setSelectedOrderItem] = useState<OrderItem | null>(null)

  const handleDuplicateCampaign = useCallback(
    async (campaignId: number, isExperiment?: boolean) => {
      try {
        setSelectedOrderItem(null)
        return await onDuplicateCampaign(campaignId, isExperiment)
      } catch (error) {
        console.error('Flight Deck: Campaign duplication failed:', error)
        throw error
      }
    },
    [onDuplicateCampaign],
  )

  if (isDismissed || alertOrders.length === 0) return null

  const count = alertOrders.length
  const dealLabel = count === 1 ? 'deal' : 'deals'
  const verbLabel = count === 1 ? 'needs' : 'need'

  return (
    <>
      <div className="w-full bg-sub-surface-bg-inverse mb-4">
        <NotificationBanner
          icon={<Icon name="TriangleAlert" className="size-6 text-warning" />}
          text={`You have ${count} ${dealLabel} that ${verbLabel} allocation.`}
          action={
            <Button
              variant="text"
              onClick={() => setIsExpanded(prev => !prev)}
              className="text-warning underline p-0 h-auto"
            >
              <Text variant="sm-sb">{isExpanded ? 'Hide Details' : 'View Details'}</Text>
            </Button>
          }
          onDismiss={() => setIsDismissed(true)}
        />

        {isExpanded && (
          <div className="px-[24px] pb-[16px]">
            <ul className="list-disc pl-[24px]">
              {alertOrders.map(order => (
                <li key={order.orderItemId} className="leading-[24px]">
                  <Text variant="base-sb" inline className="text-header-inverse">
                    {order.offeringName}
                  </Text>{' '}
                  <Button
                    variant="text"
                    onClick={() => setSelectedOrderItem(order)}
                    className="text-primary-accent underline p-0 h-auto leading-[24px]"
                  >
                    View
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {selectedOrderItem && (
        <DetailModal
          isLoading={isLoading}
          selectedOrderItem={selectedOrderItem}
          onClose={() => setSelectedOrderItem(null)}
          onCreateCampaignV2={props.onCreateCampaignV2}
          onDuplicateCampaign={handleDuplicateCampaign}
          onArchiveCampaign={onArchiveCampaign}
          open
        />
      )}
    </>
  )
}
